/**
 * useDailyTcSync — Fetches today's SBS/SUNAT exchange rate once per session.
 *
 * On mount (when firmId is available):
 *   1. Check tipo_cambio table for today's row — if exists, skip API call.
 *   2. Get Bearer token from supabase.auth.getSession().
 *   3. Fetch /api/tipo-cambio?fecha=today with Authorization header.
 *   4. Upsert into tipo_cambio with fuente='sbs'. Decimal.js for all amounts.
 *
 * Silent on failure — user can enter TC manually via TipoCambioEditor.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Decimal from 'decimal.js';
import { supabase } from '../services/supabaseClient';
import { upsertTipoCambio } from '../services/TipoCambioService';
import { assertFirmId } from '../utils/assertFirmId';

interface SbsResponse {
  compra?: number;
  venta?: number;
}

export interface DailyTcState {
  tcHoy: string | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useDailyTcSync(firmId: string | null | undefined): DailyTcState {
  const [tcHoy, setTcHoy]    = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const didFetch              = useRef(false); // prevents double-fetch in React StrictMode

  // Stable for the entire session (changes value only at calendar-day boundary)
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const syncToday = useCallback(async () => {
    if (!firmId) return;
    const fid = assertFirmId(firmId, 'useDailyTcSync');

    setLoading(true);
    try {
      // 1. Already have today's row (manual OR sbs)? Use it.
      const { data: existing } = await supabase
        .from('tipo_cambio')
        .select('venta')
        .eq('firm_id', fid)
        .eq('fecha', today)
        .eq('moneda', 'USD')
        .maybeSingle();

      if (existing) {
        setTcHoy(new Decimal(String((existing as { venta: number }).venta)).toFixed(3));
        return;
      }

      // 2. Get session Bearer token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      // 3. Fetch from SBS proxy (existing Vercel handler at api/tipo-cambio.ts)
      const res = await fetch(`/api/tipo-cambio?fecha=${today}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        signal: AbortSignal.timeout(8000),
      });
      if (!res.ok) return; // weekend/holiday/API down — silent, user enters manually

      const data = await res.json() as SbsResponse;
      if (!data.venta) return;

      const venta  = new Decimal(String(data.venta));
      const compra = data.compra != null
        ? new Decimal(String(data.compra))
        : venta.minus(new Decimal('0.010')); // fallback spread if API omits compra

      // 4. Persist in Supabase so subsequent loads skip the API call
      await upsertTipoCambio(
        fid,
        today,
        'USD',
        compra.toDecimalPlaces(4).toNumber(),
        venta.toDecimalPlaces(4).toNumber(),
        'sbs',
      );

      setTcHoy(venta.toFixed(3));
    } catch (err) {
      console.warn('[useDailyTcSync] silent fail:', err);
    } finally {
      setLoading(false);
    }
  }, [firmId, today]);

  useEffect(() => {
    if (!firmId || didFetch.current) return;
    didFetch.current = true;
    void syncToday();
  }, [firmId, syncToday]);

  return { tcHoy, loading, refetch: syncToday };
}
